import React from 'react';
import { Modal as AntModal, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useTheme } from '../../contexts/ThemeContext';

const Modal = ({ 
  title,
  children,
  open,
  loading = false,
  onCancel,
  onOk,
  okText = 'OK',
  cancelText = 'Cancelar',
  showOkButton = true,
  showCancelButton = true,
  okButtonProps = {},
  cancelButtonProps = {},
  width = 520,
  centered = true,
  style,
  className,
  variant = 'default',
  footer,
  ...props 
}) => {
  const { theme } = useTheme();

  const getVariantStyles = () => {
    const baseStyle = {
      backgroundColor: theme.cardBackground,
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    };

    const variants = {
      default: baseStyle,
      form: {
        ...baseStyle,
        width: '95vw',
        maxWidth: 1500,
      },
      confirmation: {
        ...baseStyle,
        width: width || 400,
      },
      info: {
        ...baseStyle,
        width: width || 480,
      },
      alert: {
        ...baseStyle,
        width: width || 400,
      }
    };

    return variants[variant] || variants.default;
  };

  const modalStyle = {
    ...getVariantStyles(),
    ...style,
  };

  const bodyStyle = {
    padding: '8px',
    borderRadius: '0 0 12px 12px',
    overflowX: 'hidden',
    overflowY: 'auto',
    boxSizing: 'border-box'
  };

  const headerStyle = {
    borderBottom: `1px solid ${theme.border}`,
    borderRadius: '12px 12px 0 0',
    padding: '12px',
  };

  const renderFooter = () => {
    if (footer === null) return null;
    if (footer) return footer;
    const cancelBtn = showCancelButton ? (
      <Button
        key="cancel"
        onClick={onCancel}
        style={{ borderRadius: '6px' }}
        {...cancelButtonProps}
      >
        {cancelText}
      </Button>
    ) : null;
    const okBtn = showOkButton ? (
      <Button
        key="ok"
        type="primary"
        loading={loading}
        onClick={onOk}
        style={{ 
          borderRadius: '6px',
          backgroundColor: theme.primary,
          borderColor: theme.primary,
          color: theme.white
        }}
        {...okButtonProps}
      >
        {okText}
      </Button>
    ) : null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
        {cancelBtn}
        {okBtn}
      </div>
    );
  };

  return (
    <AntModal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={renderFooter()}
      width={modalStyle.width || width}
      centered={centered}
      style={modalStyle}
      closeIcon={<CloseOutlined style={{ color: theme.textSecondary }} />}
      className={`custom-modal custom-modal--${variant} ${className || ''}`}
      styles={{
        header: headerStyle,
        body: bodyStyle,
        content: {
          overflowX: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          backdropFilter: 'blur(4px)',
        }
      }}
      destroyOnHidden
      maskClosable={variant !== 'confirmation'}
      {...props}
    >
      {children}
    </AntModal>
  );
};

Modal.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node,
  open: PropTypes.bool,
  loading: PropTypes.bool,
  onCancel: PropTypes.func,
  onOk: PropTypes.func,
  okText: PropTypes.string,
  cancelText: PropTypes.string,
  showOkButton: PropTypes.bool,
  showCancelButton: PropTypes.bool,
  okButtonProps: PropTypes.object,
  cancelButtonProps: PropTypes.object,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  centered: PropTypes.bool,
  style: PropTypes.object,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'form', 'confirmation', 'info', 'alert']),
  footer: PropTypes.node,
};

// Adicionar métodos estáticos do AntModal
Modal.confirm = AntModal.confirm;
Modal.info = AntModal.info;
Modal.success = AntModal.success;
Modal.error = AntModal.error;
Modal.warning = AntModal.warning;

export default Modal;